import React from 'react';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'framer-motion';

interface StageFrameProps {
  kicker: string;
  phase: 'problem' | 'solution';
  caption: string;
  onReplay?: () => void;
  children: React.ReactNode;
}

const StageFrame: React.FC<StageFrameProps> = ({ kicker, phase, caption, onReplay, children }) => {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-graphite shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3 sm:px-5">
        <p className="font-mono text-[0.68rem] uppercase tracking-[0.08em] text-stamp">{kicker}</p>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full border px-2.5 py-1 font-mono text-[0.68rem] uppercase tracking-wide ${
              phase === 'problem'
                ? 'border-coral/40 bg-coral-dim text-[#ffb3c0]'
                : 'border-mint/40 bg-mint-dim text-[#b8ffe8]'
            }`}
          >
            {phase === 'problem' ? t('anim.problem') : t('anim.solution')}
          </span>
          {!reduce && onReplay && (
            <button
              type="button"
              onClick={onReplay}
              className="rounded-full border border-line px-2.5 py-1 font-mono text-[0.68rem] uppercase tracking-wide text-muted hover:border-amber hover:text-amber"
            >
              {t('anim.replay')}
            </button>
          )}
        </div>
      </div>
      <div className="relative min-h-[320px] bg-[radial-gradient(ellipse_at_top,rgba(232,163,23,0.08),transparent_55%)] p-4 sm:p-6">
        {children}
      </div>
      <p className="border-t border-line px-4 py-3 text-sm text-muted sm:px-5">{caption}</p>
      {reduce && <p className="px-4 pb-4 font-mono text-[0.68rem] text-stamp sm:px-5">{t('anim.staticNote')}</p>}
    </div>
  );
};

export default StageFrame;
